import { ReactElement, useEffect, useCallback } from 'react';
import { v1 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import spinnerOverlayModel from '../../../models/spinnerOverlay';
import dataModel from '../../../models/data';
import ThemeManager from '../../../components/ThemeManager';
import Content from '../Content';
import Footer from '../Footer';
import analytics from '../../../services/analytics';
import { RequiredPolicyHiddenInputs } from '../../../components/RequiredPolicies/RequiredPolicyHiddenInputs';
import { LocalizedTextOptions } from 'interfaces/ClientInfo';
import { hasSeenWelcome } from '../../../lib/ab-tests/hasSeenWelcome';
import { WelcomeSplashVariant } from '../../../interfaces/WelcomeSplashVariant';
import { welcomePageNavigateArgs } from '../../../lib/ab-tests/welcomePageNavigateArgs';
import { redirectToBackToUrl } from '../../../lib/helpers';

import './styles.scss';

interface Props {
  oauthConsentTosLink: string;
  oauthConsentPpLink: string;
  oauthConsentNotice: LocalizedTextOptions | undefined;
}

export function SSOOnboardForm({
  oauthConsentTosLink,
  oauthConsentPpLink,
  oauthConsentNotice
}: Props): ReactElement {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();

  const {
    email,
    formAuthenticityToken,
    brandingInfo: { clientId, clientName },
    country,
    currency,
    backToUrl,
    oAuth: { redirectUri, state },
    welcomeVariant
  } = dataModel;

  const password = uuid();

  const onSubmit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      analytics.ssoOnboardingSignUpButtonClicked(
        e.currentTarget.form?.className || ''
      );
      spinnerOverlayModel.show();
    },
    [email]
  );

  const onCancel = () => {
    analytics.ssoOnboardingCancelButtonClicked();
    spinnerOverlayModel.show();
    redirectToBackToUrl({ backToUrl, redirectUri, state });
  };

  useEffect(() => {
    analytics.welcomeSplashDecided(welcomeVariant);
    analytics.ssoOnboardingLanding();
  }, [clientId]);

  if (
    !hasSeenWelcome({ search }) &&
    welcomeVariant === WelcomeSplashVariant.SHOW
  ) {
    return (
      <Navigate to={welcomePageNavigateArgs({ pathname, search })} replace />
    );
  }

  return (
    <form
      className="sso-onboard-form"
      action={`/guests${window.location.search}`}
      method="post"
      role="form"
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={formAuthenticityToken}
      />
      <input type="hidden" name="guest[partner_app]" value={clientId} />
      <input type="hidden" name="guest[email]" value={email as string} />
      <input type="hidden" name="guest[password]" value={password} />
      <input
        type="hidden"
        name="guest[password_confirmation]"
        value={password}
      />
      <input type="hidden" name="guest[country]" value={country} />
      <input type="hidden" name="guest[currency]" value={currency} />
      <RequiredPolicyHiddenInputs name="guest[accepted_policies][]" />

      <ThemeManager>
        <div className="content">
          <Content
            email={email}
            clientId={clientId}
            clientName={clientName}
            oauthConsentTosLink={oauthConsentTosLink}
            oauthConsentPpLink={oauthConsentPpLink}
            oauthConsentNotice={oauthConsentNotice}
            section1Title={t('SSOOnboardForm.content1.header')}
          />
        </div>
        <Footer onSubmit={onSubmit} onCancel={onCancel} />
      </ThemeManager>
    </form>
  );
}
